import {
  projectLineContribution,
  resolveSahLineCategory,
  sahHardshipForDate,
  type SahActiveHardship,
  type LineContributionSource,
} from './sah-fee-estimate';
import { sahContributionCapsFor } from './regulatory-rates';
import { calculateSahContribution, type SahMeansTier, type SahServiceCategory } from './sah';
import {
  SAH_ATHM_BUDGET_CAPS,
  calculateSahCarryOver,
  getSahBudgetForClient,
  splitSahBudget,
} from './award-data/sah';

export type SahBudgetScenarioMode =
  | 'ONGOING_STANDARD'
  | 'ONGOING_TRANSITIONED_HCP'
  | 'ONGOING_GRANDFATHERED'
  | 'INTERIM_STANDARD'
  | 'AT_HM_ASSISTIVE_TECHNOLOGY'
  | 'AT_HM_HOME_MODIFICATIONS'
  | 'RESTORATIVE_CARE'
  | 'END_OF_LIFE'
  | 'PRIVATE_TOP_UP';

export type SahBudgetScenarioLineBucket =
  | 'DIRECT_CARE'
  | 'CARE_MANAGEMENT'
  | 'PATHWAY'
  | 'PRIVATE_TOP_UP';

export type SahBudgetScenarioLineInput = {
  label: string;
  /** Planned tenant-local service date; funding still uses scenario.asOfDate. */
  serviceDate?: string | null;
  serviceType?: string | null;
  serviceCode?: string | null;
  sahServiceType?: string | null;
  serviceCategory?: SahServiceCategory | null;
  unitCost?: number | string | null;
  quantity?: number | string | null;
  bucket?: SahBudgetScenarioLineBucket | null;
  individualAssessedRate?: number | null;
  storedContributionPercent?: number | null;
  deemedWaiveContribution?: boolean;
  deemedWaiveFullCost?: boolean;
  /** Undefined inherits scenario hardship; null explicitly clears it for this line. */
  activeHardship?: SahActiveHardship | null;
  /** Inclusive tenant-local cap notice date, applied after rate resolution and headroom. */
  lifetimeCapReachedAt?: string | null;
  /**
   * True when this line is delivered on or after the Services Australia
   * lifetime-cap notification date recorded on the participant. A resolved
   * planned contribution is then $0. It never resolves missing evidence.
   */
  lifetimeCapReached?: boolean | null;
};

export type SahBudgetScenarioInput = {
  mode: SahBudgetScenarioMode;
  classificationLevel?: number | null;
  transitionedHcpLevel?: number | null;
  /**
   * Authoritative date-effective pathway envelope resolved by the server.
   * Required by provider-facing pathway workflows so indexed program rates are
   * not duplicated in browser code.
   */
  pathwayFundingAmount?: number | string | null;
  atHmTier?: keyof typeof SAH_ATHM_BUDGET_CAPS | null;
  restorativeUnits?: number | null;
  additionalFunding?: number | string | null;
  supplements?: number | string | null;
  carryOverFromPrior?: number | string | null;
  unspentForCarryOverEstimate?: number | string | null;
  meansTier?: SahMeansTier | null;
  /** Optional inclusive window is resolved separately on each line's tenant-local day. */
  activeHardship?: SahActiveHardship | null;
  lifetimeCapReachedAt?: string | null;
  isGrandfathered?: boolean;
  lifetimeContributionsToDate?: number | string | null;
  /**
   * Day whose funding schedule applies (YYYY-MM-DD), normally the start of the
   * quarter being planned. Omit for the current schedule.
   */
  asOfDate?: string | null;
  assessedRates?: Partial<Record<Exclude<SahServiceCategory, 'CLINICAL'>, number>>;
  lines: readonly SahBudgetScenarioLineInput[];
};

export type SahBudgetScenarioLineResult = {
  label: string;
  bucket: SahBudgetScenarioLineBucket;
  serviceCategory: SahServiceCategory | 'PRIVATE';
  grossCost: number;
  budgetDrawdown: number;
  participantContribution: number | null;
  governmentFunded: number | null;
  privateTopUp: number;
  contributionRate: number | null;
  hardshipApplied: boolean;
  contributionSource: LineContributionSource | 'private';
  warnings: string[];
};

export type SahBudgetScenarioResult = {
  mode: SahBudgetScenarioMode;
  budgetLabel: string;
  baseFunding: number;
  supplements: number;
  carryOverFromPrior: number;
  additionalFunding: number;
  grossFundingAvailable: number;
  careManagementReserve: number;
  directCareBudget: number;
  pathwayBudget: number;
  carryOverEstimate: { carryOver: number; forfeited: number } | null;
  plannedGross: number;
  directCareDrawdown: number;
  careManagementDrawdown: number;
  pathwayDrawdown: number;
  participantContribution: number | null;
  governmentFunded: number | null;
  privateTopUp: number;
  remainingDirectCare: number | null;
  remainingCareManagement: number | null;
  remainingPathway: number | null;
  hardshipApplied: boolean;
  lines: SahBudgetScenarioLineResult[];
  warnings: string[];
  sourceNotes: string[];
};

const RESTORATIVE_MAX_UNITS = 2;
function toNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toCents(value: number | string | null | undefined): number {
  const parsed = toNumber(value);
  return parsed == null ? 0 : Math.round(parsed * 100);
}

function fromCents(value: number): number {
  return Math.round(value) / 100;
}

function positiveCents(value: number | string | null | undefined): number {
  return Math.max(0, toCents(value));
}

function lineGrossCents(line: SahBudgetScenarioLineInput): number {
  const unitCostCents = positiveCents(line.unitCost);
  const quantity = toNumber(line.quantity) ?? 1;
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;
  return Math.round(unitCostCents * quantity);
}

function defaultBucket(mode: SahBudgetScenarioMode): SahBudgetScenarioLineBucket {
  switch (mode) {
    case 'AT_HM_ASSISTIVE_TECHNOLOGY':
    case 'AT_HM_HOME_MODIFICATIONS':
    case 'RESTORATIVE_CARE':
    case 'END_OF_LIFE':
      return 'PATHWAY';
    case 'PRIVATE_TOP_UP':
      return 'PRIVATE_TOP_UP';
    default:
      return 'DIRECT_CARE';
  }
}

function isOngoingMode(mode: SahBudgetScenarioMode): boolean {
  return (
    mode === 'ONGOING_STANDARD' ||
    mode === 'ONGOING_TRANSITIONED_HCP' ||
    mode === 'ONGOING_GRANDFATHERED' ||
    mode === 'INTERIM_STANDARD'
  );
}

function isPathwayMode(mode: SahBudgetScenarioMode): boolean {
  return (
    mode === 'AT_HM_ASSISTIVE_TECHNOLOGY' ||
    mode === 'AT_HM_HOME_MODIFICATIONS' ||
    mode === 'RESTORATIVE_CARE' ||
    mode === 'END_OF_LIFE'
  );
}

function resolveFunding(input: SahBudgetScenarioInput): {
  budgetLabel: string;
  baseFundingCents: number;
  careManagementReserveCents: number;
  directCareBudgetCents: number;
  pathwayBudgetCents: number;
  warnings: string[];
} {
  const warnings: string[] = [];
  const additionalFundingCents = positiveCents(input.additionalFunding);

  if (isOngoingMode(input.mode)) {
    const resolution = getSahBudgetForClient({
      classificationLevel: input.classificationLevel ?? null,
      transitionedHcpLevel:
        input.mode === 'ONGOING_STANDARD' || input.mode === 'INTERIM_STANDARD'
          ? null
          : (input.transitionedHcpLevel ?? null),
      asOfDate: input.asOfDate ?? null,
    });
    const canonicalQuarterCents = toCents(resolution.quarterly);
    if (canonicalQuarterCents === 0) {
      warnings.push('No canonical ongoing Support at Home classification budget was resolved.');
    }

    const baseFundingCents =
      input.mode === 'INTERIM_STANDARD'
        ? Math.round((canonicalQuarterCents * 60) / 100)
        : canonicalQuarterCents;
    const split = splitSahBudget(fromCents(baseFundingCents));
    const careManagementReserveCents = toCents(split.careManagementBudget);
    const directCareBudgetCents = Math.max(
      0,
      baseFundingCents +
        positiveCents(input.supplements) +
        positiveCents(input.carryOverFromPrior) +
        additionalFundingCents -
        careManagementReserveCents,
    );
    return {
      budgetLabel:
        input.mode === 'INTERIM_STANDARD'
          ? `Interim funding at 60% of ${resolution.label}`
          : resolution.label,
      baseFundingCents,
      careManagementReserveCents,
      directCareBudgetCents,
      pathwayBudgetCents: 0,
      warnings,
    };
  }

  if (input.mode === 'AT_HM_ASSISTIVE_TECHNOLOGY' || input.mode === 'AT_HM_HOME_MODIFICATIONS') {
    const tier = input.atHmTier ?? 'high';
    const baseFundingCents = toCents(SAH_ATHM_BUDGET_CAPS[tier]);
    if (input.mode === 'AT_HM_ASSISTIVE_TECHNOLOGY' && tier === 'high') {
      warnings.push(
        'High-tier assistive technology may exceed the base amount only with valid assessed need and prescription evidence.',
      );
    }
    if (input.mode === 'AT_HM_HOME_MODIFICATIONS' && tier === 'high') {
      warnings.push(
        'High-tier home modifications need lifetime and duration checks outside this scenario summary.',
      );
    }
    return {
      budgetLabel:
        input.mode === 'AT_HM_ASSISTIVE_TECHNOLOGY'
          ? `AT-HM assistive technology ${tier} tier`
          : `AT-HM home modifications ${tier} tier`,
      baseFundingCents,
      careManagementReserveCents: 0,
      directCareBudgetCents: 0,
      pathwayBudgetCents: baseFundingCents + additionalFundingCents,
      warnings,
    };
  }

  if (input.mode === 'RESTORATIVE_CARE') {
    const requestedUnits = Math.max(1, Math.round(input.restorativeUnits ?? 1));
    const units = Math.min(requestedUnits, RESTORATIVE_MAX_UNITS);
    if (requestedUnits > RESTORATIVE_MAX_UNITS) {
      warnings.push('Restorative Care Pathway funding is capped at 2 units in this scenario.');
    }
    const baseFundingCents =
      input.pathwayFundingAmount == null ? 0 : positiveCents(input.pathwayFundingAmount);
    if (baseFundingCents === 0) {
      warnings.push('No authoritative Restorative Care Pathway funding amount was resolved.');
    }
    return {
      budgetLabel: `Restorative Care Pathway (${units} unit${units === 1 ? '' : 's'})`,
      baseFundingCents,
      careManagementReserveCents: 0,
      directCareBudgetCents: 0,
      pathwayBudgetCents: baseFundingCents + additionalFundingCents,
      warnings,
    };
  }

  if (input.mode === 'END_OF_LIFE') {
    const pathwayFundingCents =
      input.pathwayFundingAmount == null ? 0 : positiveCents(input.pathwayFundingAmount);
    if (pathwayFundingCents === 0) {
      warnings.push('No authoritative End-of-Life Pathway funding amount was resolved.');
    }
    return {
      budgetLabel: 'End-of-Life Pathway',
      baseFundingCents: pathwayFundingCents,
      careManagementReserveCents: 0,
      directCareBudgetCents: 0,
      pathwayBudgetCents: pathwayFundingCents + additionalFundingCents,
      warnings,
    };
  }

  return {
    budgetLabel: 'Private top-up / non-funded scenario',
    baseFundingCents: 0,
    careManagementReserveCents: 0,
    directCareBudgetCents: 0,
    pathwayBudgetCents: 0,
    warnings,
  };
}

function contributionForLine(input: {
  line: SahBudgetScenarioLineInput;
  bucket: SahBudgetScenarioLineBucket;
  serviceCategory: SahServiceCategory;
  grossCents: number;
  scenario: SahBudgetScenarioInput;
}) {
  const { line, scenario } = input;
  const serviceDate = line.serviceDate ?? scenario.asOfDate;
  const isGrandfathered =
    scenario.isGrandfathered === true || scenario.mode === 'ONGOING_GRANDFATHERED';
  // Projection resolves zero-rate guards, dated hardship, then stored/means rates.
  // It uses the shared exact posting arithmetic before any scenario cap clipping.
  const projection = projectLineContribution({
    serviceCategory: input.serviceCategory,
    billableAmount: fromCents(input.grossCents),
    deliveryDate: serviceDate,
    asOfDate: serviceDate,
    fundingSourceCode: input.bucket === 'CARE_MANAGEMENT' ? 'CM' : null,
    deemedWaiveContribution: line.deemedWaiveContribution,
    deemedWaiveFullCost: line.deemedWaiveFullCost,
    storedContributionPercent: line.storedContributionPercent,
    activeHardship: sahHardshipForDate(
      line.activeHardship === undefined ? scenario.activeHardship : line.activeHardship,
      serviceDate,
    ),
    means: scenario.meansTier
      ? {
          tier: scenario.meansTier,
          isGrandfathered,
          assessedIndependence: line.individualAssessedRate ?? scenario.assessedRates?.INDEPENDENCE,
          assessedEverydayLiving:
            line.individualAssessedRate ?? scenario.assessedRates?.EVERYDAY_LIVING,
        }
      : null,
  });
  const costCents = line.deemedWaiveFullCost ? 0 : input.grossCents;
  // Preserve the ordinary engine's assessed-range warnings and missing-rate error.
  const rateWarnings: string[] = [];
  if (
    scenario.meansTier &&
    (projection.source === 'means_tier' || projection.source === 'needs_contribution_basis')
  ) {
    try {
      const ordinary = calculateSahContribution({
        serviceCategory: input.serviceCategory,
        serviceCost: fromCents(costCents),
        meansTier: scenario.meansTier,
        individualAssessedRate:
          line.individualAssessedRate ??
          (input.serviceCategory === 'CLINICAL'
            ? undefined
            : scenario.assessedRates?.[input.serviceCategory]),
        isGrandfathered,
        asOfDate: serviceDate,
      });
      rateWarnings.push(...ordinary.warnings);
    } catch (error) {
      rateWarnings.push(error instanceof Error ? error.message : String(error));
    }
  }
  if (projection.clientContribution == null) {
    return {
      clientContribution: null,
      governmentSubsidy: null,
      contributionRate: null,
      hardshipApplied: false,
      contributionSource: projection.source,
      costCents,
      warnings: rateWarnings.length
        ? rateWarnings
        : ['Contribution basis is missing; participant payable amount is unresolved.'],
    };
  }
  const caps = sahContributionCapsFor(serviceDate);
  const lifetimeCap = isGrandfathered ? caps.lifetimeCapGrandfathered : caps.lifetimeCapNewEntrant;
  const headroomCents = Math.max(
    0,
    toCents(lifetimeCap) - toCents(scenario.lifetimeContributionsToDate),
  );
  const roundedCents = toCents(projection.clientContribution);
  let contributionCents = Math.min(roundedCents, headroomCents);
  const warnings = [...rateWarnings];
  if (headroomCents === 0 || roundedCents >= headroomCents) {
    warnings.push('Participant contribution is limited by remaining lifetime cap headroom.');
  }
  const capNoticeDate = line.lifetimeCapReachedAt ?? scenario.lifetimeCapReachedAt;
  const capNoticeApplied =
    line.lifetimeCapReached === true ||
    Boolean(capNoticeDate && serviceDate && serviceDate >= capNoticeDate);
  if (capNoticeApplied) {
    contributionCents = 0;
  }
  return {
    clientContribution: fromCents(contributionCents),
    governmentSubsidy: fromCents(Math.max(0, costCents - contributionCents)),
    contributionRate: capNoticeApplied ? 0 : projection.effectiveRate,
    hardshipApplied: projection.source === 'hardship',
    contributionSource: projection.source,
    costCents,
    warnings,
  };
}

export function buildSahBudgetScenario(input: SahBudgetScenarioInput): SahBudgetScenarioResult {
  const funding = resolveFunding(input);
  const additionalFundingCents = positiveCents(input.additionalFunding);
  const supplementsCents = positiveCents(input.supplements);
  const carryOverFromPriorCents = positiveCents(input.carryOverFromPrior);
  const appliesOngoingExtras = isOngoingMode(input.mode);
  const effectiveSupplementsCents = appliesOngoingExtras ? supplementsCents : 0;
  const effectiveCarryOverFromPriorCents = appliesOngoingExtras ? carryOverFromPriorCents : 0;
  const defaultLineBucket = defaultBucket(input.mode);
  const warnings = [...funding.warnings];

  if (isPathwayMode(input.mode) && (supplementsCents > 0 || carryOverFromPriorCents > 0)) {
    warnings.push(
      'Ongoing supplements and carry-over are not applied to separate pathway funding ceilings.',
    );
  }

  const lines = input.lines.map<SahBudgetScenarioLineResult>((line) => {
    const bucket = line.bucket ?? defaultLineBucket;
    const grossCents = lineGrossCents(line);
    if (bucket === 'PRIVATE_TOP_UP') {
      return {
        label: line.label,
        bucket,
        serviceCategory: 'PRIVATE',
        grossCost: fromCents(grossCents),
        budgetDrawdown: 0,
        participantContribution: fromCents(grossCents),
        governmentFunded: 0,
        privateTopUp: fromCents(grossCents),
        contributionRate: null,
        hardshipApplied: false,
        contributionSource: 'private',
        warnings: [],
      };
    }

    const serviceCategory =
      resolveSahLineCategory({
        ...line,
        serviceCategory: line.serviceCategory ?? 'INDEPENDENCE',
        deliveryDate: line.serviceDate ?? input.asOfDate,
      }) ?? 'INDEPENDENCE';
    const contribution = contributionForLine({
      line,
      bucket,
      serviceCategory,
      grossCents,
      scenario: input,
    });
    return {
      label: line.label,
      bucket,
      serviceCategory,
      grossCost: fromCents(contribution.costCents),
      budgetDrawdown: fromCents(contribution.costCents),
      participantContribution: contribution.clientContribution,
      governmentFunded: contribution.governmentSubsidy,
      privateTopUp: 0,
      contributionRate: contribution.contributionRate,
      hardshipApplied: contribution.hardshipApplied,
      contributionSource: contribution.contributionSource,
      warnings: contribution.warnings,
    };
  });

  const lineWarnings = lines.flatMap((line) =>
    line.warnings.map((warning) => `${line.label}: ${warning}`),
  );
  warnings.push(...lineWarnings);

  const sumCents = (
    predicate: (line: SahBudgetScenarioLineResult) => boolean,
    amount: (line: SahBudgetScenarioLineResult) => number | null,
  ) =>
    lines.reduce((total, line) => {
      if (!predicate(line)) return total;
      const value = amount(line);
      return value == null ? total : total + toCents(value);
    }, 0);

  const plannedGrossCents = sumCents(
    () => true,
    (line) => line.grossCost,
  );
  const directCareDrawdownCents = sumCents(
    (line) => line.bucket === 'DIRECT_CARE',
    (line) => line.budgetDrawdown,
  );
  const careManagementDrawdownCents = sumCents(
    (line) => line.bucket === 'CARE_MANAGEMENT',
    (line) => line.budgetDrawdown,
  );
  const pathwayDrawdownCents = sumCents(
    (line) => line.bucket === 'PATHWAY',
    (line) => line.budgetDrawdown,
  );
  const privateTopUpCents = sumCents(
    (line) => line.bucket === 'PRIVATE_TOP_UP',
    (line) => line.privateTopUp,
  );
  const participantContributionCents = lines.some(
    (line) => line.bucket !== 'PRIVATE_TOP_UP' && line.participantContribution == null,
  )
    ? null
    : sumCents(
        () => true,
        (line) => line.participantContribution,
      );
  const governmentFundedCents = lines.some(
    (line) => line.bucket !== 'PRIVATE_TOP_UP' && line.governmentFunded == null,
  )
    ? null
    : sumCents(
        () => true,
        (line) => line.governmentFunded,
      );

  const remainingDirectCareCents =
    funding.directCareBudgetCents === 0 && directCareDrawdownCents === 0
      ? null
      : funding.directCareBudgetCents - directCareDrawdownCents;
  const remainingCareManagementCents =
    funding.careManagementReserveCents === 0 && careManagementDrawdownCents === 0
      ? null
      : funding.careManagementReserveCents - careManagementDrawdownCents;
  const remainingPathwayCents =
    funding.pathwayBudgetCents === 0 && pathwayDrawdownCents === 0
      ? null
      : funding.pathwayBudgetCents - pathwayDrawdownCents;

  if (remainingDirectCareCents != null && remainingDirectCareCents < 0) {
    warnings.push('Planned direct-care lines exceed available direct-care budget.');
  }
  if (remainingCareManagementCents != null && remainingCareManagementCents < 0) {
    warnings.push('Planned care-management lines exceed the care-management reserve.');
  }
  if (remainingPathwayCents != null && remainingPathwayCents < 0) {
    warnings.push('Planned pathway lines exceed available pathway funding.');
  }

  const carryOverEstimate =
    input.unspentForCarryOverEstimate == null
      ? null
      : calculateSahCarryOver({
          unspent: fromCents(positiveCents(input.unspentForCarryOverEstimate)),
          // Cap base is the NET quarterly base (gross classification budget minus
          // the pooled care-management reserve) plus supplements — Aged Care
          // Rules 2025 s 193-5; see calculateSahCarryOver()'s docstring. Using the
          // gross `baseFundingCents` here over-states the cap for classification
          // >= 6, same defect fixed in sah-drawdown.ts's live rollover path.
          quarterlyAllocated: fromCents(
            Math.max(0, funding.baseFundingCents - funding.careManagementReserveCents) +
              positiveCents(input.supplements),
          ),
        });

  return {
    mode: input.mode,
    budgetLabel: funding.budgetLabel,
    baseFunding: fromCents(funding.baseFundingCents),
    supplements: fromCents(supplementsCents),
    carryOverFromPrior: fromCents(carryOverFromPriorCents),
    additionalFunding: fromCents(additionalFundingCents),
    grossFundingAvailable: fromCents(
      funding.baseFundingCents +
        effectiveSupplementsCents +
        effectiveCarryOverFromPriorCents +
        additionalFundingCents,
    ),
    careManagementReserve: fromCents(funding.careManagementReserveCents),
    directCareBudget: fromCents(funding.directCareBudgetCents),
    pathwayBudget: fromCents(funding.pathwayBudgetCents),
    carryOverEstimate,
    plannedGross: fromCents(plannedGrossCents),
    directCareDrawdown: fromCents(directCareDrawdownCents),
    careManagementDrawdown: fromCents(careManagementDrawdownCents),
    pathwayDrawdown: fromCents(pathwayDrawdownCents),
    participantContribution:
      participantContributionCents == null ? null : fromCents(participantContributionCents),
    governmentFunded: governmentFundedCents == null ? null : fromCents(governmentFundedCents),
    privateTopUp: fromCents(privateTopUpCents),
    remainingDirectCare:
      remainingDirectCareCents == null ? null : fromCents(remainingDirectCareCents),
    remainingCareManagement:
      remainingCareManagementCents == null ? null : fromCents(remainingCareManagementCents),
    remainingPathway: remainingPathwayCents == null ? null : fromCents(remainingPathwayCents),
    hardshipApplied: lines.some((line) => line.hardshipApplied),
    lines,
    warnings,
    sourceNotes: [
      'Rule authority is current official Support at Home guidance, not workbook templates.',
      'Individualised budgets must align with the care plan and include funding, services, prices and contribution information.',
      'Provider price caps are not enforced by this scenario engine; applicability and compliant provider prices require separate verification.',
      'Contributions use exact decimal round(percent / 100, 6), then round(cost × rate, 2), ties away from zero; guards, dated hardship, stored/means rates, lifetime headroom, cap notice, then subsidy.',
      'Personal care contribution classification follows the supplied service date, falling back to the scenario date; undated lines keep their supplied category.',
    ],
  };
}
